// ============================================
// HEADER.JS - Control centralizado de Cart y Login
// ============================================

import React, { useState, useEffect } from "react";
import AlertBanner from "../AlertBanner";
import ShippingBanner from "../ShippingBanner";
import CookieModal from "../CookieModal";
import CookiePanel from "../CookiePanel";
import CustomerServiceModal from "../CustomerServiceModal";
import MegaMenu from "../megamenu/MegaMenu";
import MobileNavbar from "../navbar/mobileNav";
import useWindowSize from "hooks/useWindowSize";
import { useCart } from "context/CartContext";
import { useCustomer } from "context/CustomerContext";

// ✅ Cart y Login
import CartDropdown from "../cart/CartDropdown";
import LoginModal from "../account/LoginModal";
import "../cart/CartToast.css";

const Header = () => {
  const [cookieModal, setCookieModal] = useState(false);
  const [cookiePanelOpen, setCookiePanelOpen] = useState(false);
  const { width: windowWidth } = useWindowSize();
  // Inicializar desde el primer render: si arranca en null, `is-mobile`
  // (min-height 60px) llega un frame tarde y todo el contenido brinca.
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 746
  );

  // ✅ Obtener estado del cart desde Context
  const { cartCount, setCartCount, isCartLoading, notification } = useCart();

  // ✅ Estado centralizado para Cart dropdown
  const [isCartOpen, setIsCartOpen] = useState(false);

  // ✅ Estado centralizado para Login
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  // ✅ Estado para verificar si el usuario está logueado
  const { isAuthenticated, customer } = useCustomer();
  const isLoggedIn = isAuthenticated;
  const userName = customer?.first_name || '';

  useEffect(() => {
    const cookieShowed = localStorage.getItem("cookieShowed");
    if (!cookieShowed) {
      setCookieModal(true);
    }
  }, []);

  // Auth state now comes from CustomerContext — no localStorage polling needed

  const handleDismiss = () => {
    localStorage.setItem("cookieShowed", "true");
    setCookieModal(false);
  };

  const handleOpenCookieSettings = () => {
    setCookieModal(false);
    localStorage.setItem("cookieShowed", "true");
    setCookiePanelOpen(true);
  };

  useEffect(() => {
    if (typeof windowWidth === "number") {
      const mobile = windowWidth < 746;
      setIsMobile(mobile);

      // When switching to mobile, clear scroll-reverse classes that MegaMenu may have left
      if (mobile) {
        const headerSticky = document.querySelector('.header-sticky-wrapper');
        if (headerSticky) {
          headerSticky.classList.remove('scroll-down', 'scroll-up');
          headerSticky.classList.add('at-top');
        }
      }
    }
  }, [windowWidth]);

  // ✅ Funciones para el Cart
  const openCart = () => {
    if (isLoginOpen) setIsLoginOpen(false);
    setIsCartOpen(true);
  };

  const closeCart = () => {
    setIsCartOpen(false);
  };

  const updateCartCount = (count) => {
    setCartCount(count);
  };

  // ✅ Funciones para el Login
  const openLogin = () => {
    if (isCartOpen) setIsCartOpen(false);
    setIsLoginOpen(true);
  };

  const closeLogin = () => {
    setIsLoginOpen(false);
  };

  // Permite abrir el login desde otros componentes (p. ej. el chat):
  // window.dispatchEvent(new Event('phys:open-login'))
  useEffect(() => {
    const handler = () => {
      setIsCartOpen(false);
      setIsLoginOpen(true);
    };
    window.addEventListener('phys:open-login', handler);
    return () => window.removeEventListener('phys:open-login', handler);
  }, []);

  return (
    <>
      {/* Modals - Highest z-index */}
      <CustomerServiceModal />
      {cookieModal && (
        <CookieModal
          setCookieModal={handleDismiss}
          onOpenSettings={handleOpenCookieSettings}
        />
      )}
      <CookiePanel
        isOpen={cookiePanelOpen}
        onClose={() => setCookiePanelOpen(false)}
      />

      {/* ✅ Login Modal */}
      <LoginModal 
        isOpen={isLoginOpen}
        onClose={closeLogin}
      />

      {/* ✅ Cart Push-Down */}
      <CartDropdown 
        isOpen={isCartOpen}
        onClose={closeCart}
        onUpdateCount={updateCartCount}
      />

      {/* ✅ Notificación de producto agregado — banner full-width slide-down */}
      {notification && (
        <div className="phys-cart-toast">
          <div className="phys-cart-toast__inner">
            <div className="phys-cart-toast__thumb">
              {notification.thumbnail ? (
                <img src={notification.thumbnail} alt="" />
              ) : (
                <div className="phys-cart-toast__thumb-empty" />
              )}
            </div>
            <div className="phys-cart-toast__body">
              <span className="phys-cart-toast__eyebrow">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Agregado al carrito
              </span>
              <p className="phys-cart-toast__title">{notification.productName}</p>
              {(notification.variantLabel || notification.price) && (
                <p className="phys-cart-toast__meta">
                  {notification.variantLabel && (
                    <span>{notification.variantLabel}</span>
                  )}
                  {notification.variantLabel && notification.price && (
                    <span aria-hidden="true"> · </span>
                  )}
                  {notification.price && <span>${notification.price}</span>}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Announcement Banners */}
      <div className="announcement-banners">
        <AlertBanner />
        <ShippingBanner />
      </div>
      {/* Header - Sticky with scroll reverse */}
      {/* Sin AnimatedSection: su `transform` convertía este wrapper en el
          contenedor del navbar móvil (position: fixed) y lo hacía aparecer
          desplazado y luego brincar a su sitio. */}
      <div className={`header-sticky-wrapper ${isMobile ? 'is-mobile' : ''}`}>
        <div className="phys-header-content">
          <div className="phys-desktop-nav">
            <MegaMenu
              onCartClick={openCart}
              cartCount={cartCount}
              isCartLoading={isCartLoading}
              onLoginClick={openLogin}
              isLoggedIn={isLoggedIn}
              userName={userName}
            />
          </div>
          <div className="phys-mobile-nav">
            <MobileNavbar
              onCartClick={openCart}
              cartCount={cartCount}
              isCartLoading={isCartLoading}
              onLoginClick={openLogin}
              isLoggedIn={isLoggedIn}
              userName={userName}
            />
          </div>
        </div>
      </div>

      {/* CSS para la animación */}
      <style>{`
        @keyframes slide-in {
          from {
            opacity: 0;
            transform: translateX(100px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-slide-in {
          animation: slide-in 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default Header;